import { FC, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import { isEmpty } from 'lodash';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { DelimitedList } from '@app/components';
import { sharedMessages } from '@app/translations';

import './CourseHero.scss';

interface CourseHeroProps {
  courseName: string;
  department?: string;
  courseNumber?: string;
  description?: string;
  instructors?: string[];
  location?: string;
}

const instructorsListLimit = 3;
const cnBem = createBemBlockBuilder(['courseHero']);

export const CourseHero: FC<CourseHeroProps> = ({
  courseName,
  courseNumber,
  department,
  description,
  instructors = [],
  location,
}) => {
  const heroTitle = courseNumber
    ? `${courseNumber} - ${courseName}`
    : courseName;

  const getCoursePropertyInfo = <T,>({
    label,
    value,
    formatter,
  }: {
    label: keyof typeof sharedMessages;
    value: T;
    formatter?: (value: T) => ReactElement;
  }) => (
    <FormattedMessage {...sharedMessages[label]}>
      {message => (
        <div
          className={cnBem('__courseInfoProperty')}
          data-auto="course-info-property"
        >
          <strong className={cnBem('__label')}>{`${message}: `}</strong>
          {formatter ? (
            formatter(value)
          ) : (
            <span className={cnBem('__value')}>{value}</span>
          )}
        </div>
      )}
    </FormattedMessage>
  );

  return (
    <div data-auto="course-hero" className={cnBem()}>
      <h1 className={cnBem('__title')}>{heroTitle}</h1>
      {department &&
        getCoursePropertyInfo({ label: 'department', value: department })}
      {!isEmpty(instructors) &&
        getCoursePropertyInfo({
          label: 'instructor',
          value: instructors,
          formatter: items => (
            <DelimitedList
              items={items}
              limit={instructorsListLimit}
              delimiter=", "
            />
          ),
        })}
      {location &&
        getCoursePropertyInfo({
          label: 'location',
          value: location,
        })}
      {description && <p className={cnBem('__description')}>{description}</p>}
    </div>
  );
};
